import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

interface ScoreGaugeProps {
  score: number;
  showLegend?: boolean;
}

export interface ScoreLegendItem {
  label: string;
  min: number;
  max: number;
  color: string;
}

export const SCORE_LEGEND_ITEMS: ScoreLegendItem[] = [
  {
    label: 'BAIXO RISCO',
    min: 751,
    max: 1000,
    color: '#4caf50',
  },
  {
    label: 'RISCO MODERADO ',
    min: 501,
    max: 750,
    color: '#facc15',
  },
  {
    label: 'ATENÇÃO COMERCIAL',
    min: 251,
    max: 500,
    color: '#ff9800',
  },
  {
    label: 'ALTO RISCO',
    min: 0,
    max: 250,
    color: '#ef5350',
  },
];

export const ScoreGauge = ({ score, showLegend = true }: ScoreGaugeProps) => {
  const scoreNumber = Number(score) || 0;
  const targetPercentage = Math.min(Math.max(scoreNumber / 1000, 0), 1);

  const [percentage, setPercentage] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);

  useEffect(() => {
    if (score === undefined || score === null) return;

    // Start gauge transition in next frame
    const timer = setTimeout(() => {
      setPercentage(targetPercentage);
    }, 100);

    // Number count-up animation
    let startTimestamp: number | null = null;
    const duration = 1200; // 1.2 seconds to match gauge transition
    let animationFrameId: number;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);

      // Easing function: easeOutQuad: progress * (2 - progress)
      const easeProgress = progress * (2 - progress);

      setDisplayScore(Math.floor(easeProgress * scoreNumber));

      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      } else {
        setDisplayScore(scoreNumber);
      }
    };

    animationFrameId = window.requestAnimationFrame(step);

    return () => {
      clearTimeout(timer);
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [score, scoreNumber, targetPercentage]);

  const activeLength = percentage * 251.3;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1.5 }}>
        <Typography
          variant="h2"
          fontWeight={900}
          className="score-display-number"
          sx={{
            color: '#1e293b',
            fontSize: '3rem',
            letterSpacing: '-1px',
            lineHeight: 1,
            mb: 0.25,
          }}
        >
          {displayScore}
        </Typography>
      </Box>

      <Box className="score-gauge-svg-container" sx={{ position: 'relative', width: 280, height: 145, display: 'flex', justifyContent: 'center' }}>
        <svg width="100%" height="100%" viewBox="0 0 200 135" style={{ fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', sans-serif" }}>
          {/* Gradients */}
          <defs>
            <linearGradient id="gauge-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ef5350" />    {/* Red (0-250) */}
              <stop offset="33%" stopColor="#ff9800" />   {/* Orange (251-500) */}
              <stop offset="67%" stopColor="#facc15" />   {/* Yellow (501-750) */}
              <stop offset="100%" stopColor="#4caf50" />  {/* Green (751-1000) */}
            </linearGradient>
          </defs>

          {/* Background track */}
          <path
            d="M 20 110 A 80 80 0 0 1 180 110"
            fill="none"
            stroke="rgba(0, 0, 0, 0.05)"
            strokeWidth="14"
            strokeLinecap="round"
          />

          {/* Tick pattern overlay */}
          <path
            d="M 20 110 A 80 80 0 0 1 180 110"
            fill="none"
            stroke="url(#gauge-gradient)"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray="2 4"
            opacity="0.4"
          />

          {/* Active Value Arc */}
          <path
            d="M 20 110 A 80 80 0 0 1 180 110"
            fill="none"
            stroke="url(#gauge-gradient)"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray="251.3"
            style={{
              strokeDashoffset: 251.3 - activeLength,
              transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />

          {/* Center Pin */}
          <circle cx="100" cy="110" r="8" fill="#1e293b" />

          {/* Needle */}
          <line
            x1="100"
            y1="110"
            x2="28"
            y2="110"
            stroke="#1e293b"
            strokeWidth="3.5"
            strokeLinecap="round"
            style={{
              transform: `rotate(${percentage * 180}deg)`,
              transformOrigin: '100px 110px',
              transition: 'transform 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />

          {/* Tick: 0 */}
          <line x1="14" y1="110" x2="26" y2="110" stroke="rgba(0,0,0,0.2)" strokeWidth="1.2" />
          <text x="20" y="128" textAnchor="middle" fill="#475569" fontSize="9" fontWeight="700">0</text>

          {/* Tick: 250 */}
          <line x1="39.2" y1="49.2" x2="47.7" y2="57.7" stroke="rgba(0,0,0,0.2)" strokeWidth="1.2" />
          <text x="25" y="42" textAnchor="middle" fill="#475569" fontSize="9" fontWeight="700">250</text>

          {/* Tick: 500 */}
          <line x1="100" y1="24" x2="100" y2="36" stroke="rgba(0,0,0,0.2)" strokeWidth="1.2" />
          <text x="100" y="12" textAnchor="middle" fill="#475569" fontSize="9" fontWeight="700">500</text>

          {/* Tick: 750 */}
          <line x1="160.8" y1="49.2" x2="152.3" y2="57.7" stroke="rgba(0,0,0,0.2)" strokeWidth="1.2" />
          <text x="175" y="42" textAnchor="middle" fill="#475569" fontSize="9" fontWeight="700">750</text>

          {/* Tick: 1000 */}
          <line x1="174" y1="110" x2="186" y2="110" stroke="rgba(0,0,0,0.2)" strokeWidth="1.2" />
          <text x="180" y="128" textAnchor="middle" fill="#475569" fontSize="9" fontWeight="700">1000</text>
        </svg>
      </Box>

      {showLegend && (
        <Box
          className="score-legend-container"
          sx={{
            width: '100%',
            mt: 1.5,
            pt: 1.75,
            borderTop: '1px solid #ece7f2',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.85,
          }}
        >

          {SCORE_LEGEND_ITEMS.map((item) => {
            const isActive = scoreNumber >= item.min && scoreNumber <= item.max;
            return (
              <Box
                key={item.label}
                className="score-legend-item"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  py: 0.25,
                  px: 0.75,
                  borderRadius: '6px',
                  backgroundColor: isActive ? 'rgba(114, 57, 137, 0.05)' : 'transparent',
                }}
              >
                <Box
                  className="score-legend-bar"
                  sx={{
                    width: 24,
                    height: 6,
                    borderRadius: '3px',
                    backgroundColor: item.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  className="score-legend-label"
                  sx={{
                    color: isActive ? '#1e293b' : '#475569',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.75rem',
                    lineHeight: 1.3,
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
